import { useContext, useState, useEffect } from 'react';
import { useParams, RouterContext, goTo } from 't-lib-ui/router';
import MainLayout from '../../components/MainLayout';
import AdminHeader from '../../components/admin/AdminHeader';
import { FlowForm, FlowFormLeaveContext, ButtonBar, useFlowFormGlobal, registerKeyHandler, unregisterKeyHandler, useConfirmationModal } from 't-lib-ui/structure';
import { getAdminMagicSecret, getCdnUrl } from '../../connector/auth';
import { AppState } from '../../context/general';
import { TextButton, Input, TextArea, Note, useToast, Option } from 't-lib-ui/widgets';
import { chevronBackIcon, sparklesIcon } from '../../components/icons';
import AdminSecretModal from '../../components/admin/AdminSecretModal';
import { sidebarLeaveHandler } from '../../components/LeftSidebarFocus';
import { useGetStuff, useGenerateImage } from '../../connector/api';
import { ImageCollection, ImageGenerationModel } from '../../schema-copy/api-schema';
import { QueryStatus } from 't-lib-ui/connector';
import './AdminEditImagePage.css';

const AdminEditImagePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const routerCtx = useContext(RouterContext);
  const appState = useContext(AppState);
  const cdnUrl = getCdnUrl(appState.state.authData!);
  const [adminSecret, setAdminSecret] = useState<string | null>(null);
  const [imageName, setImageName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
  const [showToast, toastProps] = useToast();
  const [recentPrompts, setRecentPrompts] = useState<string[]>([]);
  const [model, setModel] = useState<ImageGenerationModel>('google/gemini-3.1-flash-image-preview');
  const [pastedImage, setPastedImage] = useState<string | null>(null);

  const magic = getAdminMagicSecret() || adminSecret || '';
  const isNew = id === 'new';

  const generateImage = useGenerateImage(magic);

  useFlowFormGlobal();

  // Load recent prompts from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('adminImageRecentPrompts');
      if (saved) {
        const prompts = JSON.parse(saved);
        setRecentPrompts(Array.isArray(prompts) ? prompts : []);
      }
    } catch (error) {
      console.error('Error loading recent prompts:', error);
    }
  }, []);

  // Save prompt to recent prompts
  const savePromptToRecent = (prompt: string) => {
    try {
      const trimmedPrompt = prompt.trim();
      if (!trimmedPrompt) return;

      // Remove duplicates and add to front
      const newPrompts = [trimmedPrompt, ...recentPrompts.filter(p => p !== trimmedPrompt)].slice(0, 10);
      setRecentPrompts(newPrompts);
      localStorage.setItem('adminImageRecentPrompts', JSON.stringify(newPrompts));
    } catch (error) {
      console.error('Error saving recent prompts:', error);
    }
  };

  // Handle clipboard paste for images
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of Array.from(items)) {
        if (!item.type.startsWith('image/')) continue;
        const file = item.getAsFile();
        if (!file) continue;
        e.preventDefault();
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result == 'string')
            setPastedImage(reader.result);
        };
        reader.readAsDataURL(file);
        return;
      }
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  useEffect(() => {
    registerKeyHandler('adminEditImagePage', {
      'Escape': () => {
        goTo(routerCtx, '/a/admin/images');
        return true;
      },
    }, false, 0);

    return () => unregisterKeyHandler('adminEditImagePage');
  }, [routerCtx]);

  // Load image collection to check existing images
  const { data: imageCollection, status: loadStatus } = useGetStuff<ImageCollection>('image-collection', magic);

  // Initialize form when editing existing image
  useEffect(() => {
    if (!isNew && imageCollection?.images) {
      const existingImage = imageCollection.images.find(img => img.name === id);
      if (existingImage) {
        // When editing, pre-populate with incremented name to avoid overwriting
        const existingNames = imageCollection.images.map(img => img.name);
        const incrementedName = getNextAvailableName(existingImage.name, existingNames);
        setImageName(incrementedName);

        // Use w1250 version for preview
        const previewUrl = existingImage.url.replace(/\.(png|jpg|jpeg|webp|gif)$/i, '-w1250.webp');
        setCurrentImageUrl(previewUrl);
      } else if (loadStatus !== QueryStatus.Loading) {
        // Image not found in collection but not loading - might be newly created
        // Try to construct URL based on naming convention
        const constructedUrl = `${cdnUrl}images/${id}.png`;
        const previewUrl = constructedUrl.replace(/\.(png|jpg|jpeg|webp|gif)$/i, '-w1250.webp');
        setCurrentImageUrl(previewUrl);
        setImageName(id);
      }
    }
  }, [id, isNew, imageCollection, loadStatus, cdnUrl]);

  const handleBack = () => goTo(routerCtx, '/a/admin/images');

  // Wait for image to be available on CDN by polling (max 30s, min 400ms between checks)
  // Uses Image element to avoid CORB — browsers allow cross-origin image loads without CORS headers
  const waitForImageAvailability = async (imageUrl: string): Promise<boolean> => {
    const checkImage = (url: string) => new Promise<boolean>(resolve => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = `${url}?_=${Date.now()}`; // cache-bust to avoid browser caching 404
    });

    const deadline = Date.now() + 30_000;
    while (Date.now() < deadline) {
      const checkStart = Date.now();
      if (await checkImage(imageUrl)) return true;
      const elapsed = Date.now() - checkStart;
      const wait = Math.max(0, 400 - elapsed);
      if (wait > 0) await new Promise(resolve => setTimeout(resolve, wait));
    }
    return false;
  };

  // Generate next available name for duplicates
  const getNextAvailableName = (baseName: string, existingNames: string[]): string => {
    // Check if name ends with a number
    const match = baseName.match(/^(.+?)(\d+)$/);

    if (match) {
      // Name ends with number, increment it
      const prefix = match[1];
      let num = parseInt(match[2], 10);
      let newName: string;

      do {
        num++;
        newName = `${prefix}${num}`;
      } while (existingNames.includes(newName));

      return newName;
    } else {
      // Name doesn't end with number, add '1'
      let newName = `${baseName}1`;
      let counter = 1;

      while (existingNames.includes(newName)) {
        counter++;
        newName = `${baseName}${counter}`;
      }

      return newName;
    }
  };

  const handleGenerate = async () => {
    if (!imageName.trim() || !prompt.trim()) {
      showToast({ children: 'Please provide both image name and prompt', type: 'error' }, 3000);
      return;
    }

    try {
      setGenerating(true);
      setGeneratedImageUrl(null);

      // Check if name already exists and adjust if needed
      const existingNames = imageCollection?.images.map(img => img.name) || [];
      let finalName = imageName.trim();

      // If we're modifying an existing image (editing with inputImage), always increment to avoid overwriting
      if (!isNew && existingNames.includes(finalName)) {
        finalName = getNextAvailableName(finalName, existingNames);
        setImageName(finalName);
        showToast({ children: `Creating new image: "${finalName}"`, type: 'info' }, 4000);
      } else if (isNew && existingNames.includes(finalName)) {
        // New image but name already exists
        finalName = getNextAvailableName(finalName, existingNames);
        setImageName(finalName);
        showToast({ children: `Name exists, using "${finalName}" instead`, type: 'info' }, 4000);
      }

      // Save prompt to recent prompts before generating
      savePromptToRecent(prompt.trim());

      const result = await generateImage({
        name: finalName,
        prompt: prompt.trim(),
        inputImage: !isNew && currentImageUrl ? id : undefined,
        pastedImageBase64: pastedImage || undefined,
        model,
      });

      const previewUrl = result.url.replace(/\.(png|jpg|jpeg|webp|gif)$/i, '-w1250.webp');

      showToast({ children: 'Image generated successfully! Waiting for CDN...', type: 'success' }, 3000);

      // Wait for image to be available on CDN before showing/redirecting
      const isAvailable = await waitForImageAvailability(previewUrl);

      // Only show the image once we know it's on the CDN; cache-bust to avoid browser-cached 404
      setGeneratedImageUrl(`${previewUrl}?_=${Date.now()}`);

      if (isAvailable) {
        showToast({ children: 'Image ready! Redirecting in 2 seconds...', type: 'success' }, 2000);
        // Add extra delay to ensure image collection is updated
        setTimeout(() => {
          goTo(routerCtx, `/a/admin/image/${result.name}`);
        }, 2000);
      } else {
        // If timeout, still redirect but show warning
        showToast({ children: 'Redirecting in 2 seconds (image may still be uploading)...', type: 'info' }, 2000);
        setTimeout(() => {
          goTo(routerCtx, `/a/admin/image/${result.name}`);
        }, 2000);
      }
    } catch (error: any) {
      console.error('Error generating image:', error);
      showToast({
        children: error?.message || 'Failed to generate image',
        type: 'error'
      }, 5000);
    } finally {
      setGenerating(false);
    }
  };

  const showConfirmGenerate = useConfirmationModal({
    title: 'Generate Image?',
    confirmContent: 'Generate',
    dismissContent: 'Cancel',
    children: `This will use AI to generate an image. This creates costs. Continue?`,
    onConfirm: handleGenerate
  });

  const loading = loadStatus === QueryStatus.Loading;
  const canGenerate = imageName.trim().length > 0 && prompt.trim().length > 0;

  return (
    <>
      <AdminSecretModal onSecretVerified={setAdminSecret} />
      {adminSecret && (
        <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
        <MainLayout
          selectedFeed="admin-images"
          right={({ onExpand }) => (
            <FlowForm main={true} className='full-margin'>
              <div className="adminEditImageContainer">
                <AdminHeader title={isNew ? 'Create New Image' : `Edit Image: ${id}`} onExpand={onExpand} />

                <ButtonBar size="small">
                  <TextButton
                    label="Back"
                    icon={chevronBackIcon}
                    onClick={handleBack}
                    focussable="flow"
                  />
                  {!isNew && (
                    <TextButton
                      label="New Image"
                      onClick={() => goTo(routerCtx, '/a/admin/image/new')}
                      focussable="flow"
                    />
                  )}
                  <TextButton
                    label="Generate with AI"
                    icon={sparklesIcon}
                    onClick={showConfirmGenerate}
                    focussable="flow"
                    type="major"
                    busy={generating}
                    enabled={canGenerate ? 'enabled' : 'disabled'}
                  />
                </ButtonBar>

                <Note {...toastProps} />

                {loading && (
                  <div className="adminEditImageLoading">
                    Loading...
                  </div>
                )}

                {!loading && (
                  <div className="adminEditImageForm">
                    <div className="adminEditImageField">
                      <label className="adminEditImageLabel">
                        Image Name
                      </label>
                      <Input
                        value={imageName}
                        onInput={setImageName}
                        placeholder="my-image-name"
                        layout="singleCol"
                      />
                      <div className="adminEditImageHint">
                        Alphanumeric, hyphens, and underscores only. Will be saved as PNG.
                      </div>
                    </div>

                    {!isNew && currentImageUrl && !generatedImageUrl && (
                      <div className="adminEditImageField">
                        <label className="adminEditImageLabel">
                          Current Image
                        </label>
                        <div className="adminEditImagePreview">
                          <a href={currentImageUrl.replace('-w1250.webp', '.png')} target="_blank" rel="noopener noreferrer">
                            <img
                              src={currentImageUrl}
                              alt={imageName}
                              onError={(e) => {
                                // Fallback to original if w1250 doesn't exist
                                const originalUrl = currentImageUrl.replace('-w1250.webp', '.png');
                                (e.target as HTMLImageElement).src = originalUrl;
                              }}
                            />
                          </a>
                        </div>
                        <div className="adminEditImageHint">
                          Click to view full size. If you provide a prompt, the AI will modify this image.
                        </div>
                      </div>
                    )}

                    {generatedImageUrl && (
                      <div className="adminEditImageField">
                        <label className="adminEditImageLabel">
                          Generated Image
                        </label>
                        <div className="adminEditImagePreview">
                          <a href={generatedImageUrl.replace('-w1250.webp', '.png')} target="_blank" rel="noopener noreferrer">
                            <img
                              src={generatedImageUrl}
                              alt={imageName}
                            />
                          </a>
                        </div>
                        <div className="adminEditImageHint">
                          Click to view full size. Redirecting to image page...
                        </div>
                      </div>
                    )}

                    <div className="adminEditImageField">
                      <label className="adminEditImageLabel">
                        Reference Image (paste from clipboard)
                      </label>
                      {pastedImage ? (
                        <>
                          <div className="adminEditImagePreview">
                            <img src={pastedImage} alt="Pasted reference" style={{ maxHeight: '300px', objectFit: 'contain' }} />
                          </div>
                          <TextButton
                            label="Remove pasted image"
                            onClick={() => setPastedImage(null)}
                            focussable="flow"
                            size="small"
                            type="minor"
                          />
                        </>
                      ) : (
                        <div className="adminEditImageHint">
                          Copy an image to your clipboard and paste (Ctrl/Cmd+V) anywhere on this page to use it as a reference.
                        </div>
                      )}
                    </div>

                    <div className="adminEditImageField">
                      <label className="adminEditImageLabel">Model</label>
                      {([
                        { id: 'google/gemini-2.5-flash-image', label: 'Gemini 2.5 Flash' },
                        { id: 'google/gemini-3.1-flash-image-preview', label: 'Gemini 3.1 Flash' },
                        { id: 'google/gemini-3-pro-image-preview', label: 'Gemini 3 Pro ($$$)' },
                        { id: 'openai/gpt-5.4-image-2', label: 'GPT 5.4 Image 2 ($$)' },
                      ] as const).map(m => (
                        <Option key={m.id} type="radio" label={m.label}
                          checked={model == m.id}
                          onToggle={() => setModel(m.id)}
                        />
                      ))}
                    </div>

                    <div className="adminEditImageField">
                      <label className="adminEditImageLabel">
                        AI Prompt
                      </label>
                      <TextArea
                        value={prompt}
                        onInput={setPrompt}
                        placeholder={isNew
                          ? "Describe the image you want to generate (e.g., 'A futuristic cityscape at sunset with flying cars')"
                          : "Describe how to modify the current image (e.g., 'Make it darker and add rain')"
                        }
                        rows={6}
                        layout="singleCol"
                        borderless={false}
                      />
                      <div className="adminEditImageHint">
                        {isNew
                          ? 'Describe the image you want the AI to create. Generated images will be 21:9 aspect ratio.'
                          : 'Describe how the AI should modify the current image. Leave empty to generate a completely new image.'
                        }
                      </div>
                      {recentPrompts.length > 0 && (
                        <div style={{ marginTop: '12px' }}>
                          <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px', color: 'var(--gray-400)' }}>
                            Recent prompts:
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {recentPrompts.map((recentPrompt, index) => (
                              <TextButton
                                key={index}
                                label={recentPrompt.length > 50 ? `${recentPrompt.substring(0, 50)}…` : recentPrompt}
                                onClick={() => setPrompt(recentPrompt)}
                                focussable="flow"
                                size="small"
                                type="minor"
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="adminEditImageNote">
                      <strong>Note:</strong> AI image generation incurs costs. Generated images are saved as PNG
                      in 21:9 aspect ratio with scaled WebP versions (286px and 1250px width) automatically created.
                    </div>
                  </div>
                )}
              </div>
            </FlowForm>
          )}
          startWithLeftCollapsed={false}
        />
        </FlowFormLeaveContext.Provider>
      )}
    </>
  );
};

export default AdminEditImagePage;
